import React from 'react';
import { fromPairs, map, includes, isEmpty } from 'lodash';
import classNames from 'classnames';

// local dependencies
import { Form } from 'modules/form';
import { button as Button } from 'modules/button';
import { Icon } from 'modules/icon-bank';
import { spinner as Spinner } from 'ui/views/generic/spinner';
import { textWithPatternMatch } from 'utils/textWithPatternMatch';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { accessManagementUserGroupsCreateModeUsersTab as AccessManagementUserGroupsCreateModeUsersTab } from 'ui/views/widgets/accessManagementUserGroupsCreateModeUsersTab';
 *
 * // render
 * <AccessManagementUserGroupsCreateModeUsersTab propName='propValue'/>
 */
export const accessManagementUserGroupsCreateModeUsersTab = ( {
    selectedTabObj,
    isSearchingUsers,
    usersSearchQuery,
    userSearchResults,
    addedUsers,
    onUserSearch,
    onUserAdd,
    onUserRemove,
    onSubmitFormData,
    onCancelActionClick,
    onGoToInformationActionClick
} ) => {
    return (
        <div className='ui-access-management-user-groups-create-mode-users-tab'>
            <Form
                fields={ fromPairs( map( selectedTabObj.searchSection.fields, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
                mode='form'
                onChangeCallBack={ onUserSearch }
                onSubmit={ () => onSubmitFormData( selectedTabObj ) }
            >
                { ( { fields, values, onChange, onSumbit } = {} ) => {

                    return (
                        <form
                            onSubmit={ onSumbit }
                            onKeyDown={ ( event ) => { if( 'Enter' === event.key ) { event.preventDefault(); event.stopPropagation(); } } } // stop implicit form submission on Enter key press
                            className='ui-access-management-user-groups-create-mode-users-tab__form'
                        >
                            { /* fields */ }
                            { map( fields, ( field ) => {

                                const fieldClassName = classNames( 'ui-access-management-user-groups-create-mode-users-tab__form__field', `ui-access-management-user-groups-create-mode-users-tab__form__${field.name}` );

                                return (
                                    <field.view
                                        className={ fieldClassName }
                                        key={ field.name }
                                        name={ field.name }
                                        placeholder={ field.placeholder }
                                        onChange={ onChange }
                                        value={ values[ field.name ] }
                                        showLabel={ false }
                                        config={ field.config }
                                        prefix={ isSearchingUsers ? <Spinner radius={15} /> : <Icon id='search' /> }
                                        searchPrefix={ true }
                                    />
                                );
                            } ) }

                            {/* Search Results */}
                            {
                                ! isEmpty( userSearchResults ) && (
                                    <div className='ui-access-management-user-groups-create-mode-users-tab__form__search-results'>
                                        {
                                            map( userSearchResults, user => {
                                                return (
                                                    <div key={ user.id } className='ui-access-management-user-groups-create-mode-users-tab__form__search-results__result'>
                                                        <div className='ui-access-management-user-groups-create-mode-users-tab__form__search-results__result__info'>
                                                            <div className='ui-access-management-user-groups-create-mode-users-tab__form__search-results__result__info__name'>
                                                                { user.name }
                                                            </div>
                                                            <div className='ui-access-management-user-groups-create-mode-users-tab__form__search-results__result__info__email'>
                                                                { user.email }
                                                            </div>
                                                        </div>
                                                        <div
                                                            className='ui-access-management-user-groups-create-mode-users-tab__form__search-results__result__icon'
                                                            onClick={ () => onUserAdd( user ) }
                                                        >
                                                            <Icon id='addUserIcon' />
                                                        </div>
                                                    </div>
                                                );
                                            } )
                                        }
                                    </div>
                                )
                            }

                            {/* Added Users */}
                            {
                                ! isEmpty( addedUsers ) && (
                                    <div className='ui-access-management-user-groups-create-mode-users-tab__form__added-users'>
                                    {
                                            map( addedUsers, user => {
                                                return (
                                                    <div key={ user.id } className='ui-access-management-user-groups-create-mode-users-tab__form__added-users__user'>
                                                        <div className='ui-access-management-user-groups-create-mode-users-tab__form__added-users__user__info'>
                                                            <div
                                                                className='ui-access-management-user-groups-create-mode-users-tab__form__added-users__user__info__name'
                                                                dangerouslySetInnerHTML={ { __html: usersSearchQuery ? textWithPatternMatch( {
                                                                    value: user.name,
                                                                    pattern: usersSearchQuery
                                                                } ) : user.name } }
                                                            />
                                                            <div
                                                                className='ui-access-management-user-groups-create-mode-users-tab__form__added-users__user__info__email'
                                                                dangerouslySetInnerHTML={ { __html: usersSearchQuery ? textWithPatternMatch( {
                                                                    value: user.email,
                                                                    pattern: usersSearchQuery
                                                                } ) : user.email } }
                                                            />
                                                        </div>
                                                        <div
                                                            className='ui-access-management-user-groups-create-mode-users-tab__form__added-users__user__icon'
                                                            onClick={ () => onUserRemove( user ) }
                                                        >
                                                            <Icon id='removeUserIcon' />
                                                        </div>
                                                    </div>
                                                );
                                            } )
                                        }
                                    </div>
                                )
                            }

                            
                            {/* button actions */}
                            <div className='ui-access-management-user-groups-create-mode-users-tab__form__actions'>
                                { map( selectedTabObj.actions, action => {
                                    const clickHandler = 'cancel' === action.name ? onCancelActionClick : onGoToInformationActionClick;

                                    return (
                                        <Button
                                            key={ action.name }
                                            id= { action.name }
                                            title={ action.title }
                                            size='large'
                                            type={ 'submit' !== action.name ? 'button' : 'submit' }
                                            outline={ includes( [ 'cancel', 'goToInformation' ], action.name ) ? true : false }
                                            onClick={ 'submit' === action.name ? null : clickHandler }
                                            showIcon={ 'goToInformation' === action.name }
                                            iconId='backward'
                                            className='ui-access-management-user-groups-create-mode-users-tab__form__actions__action'
                                        />
                                    );
                                } ) }
                            </div>
                        </form>
                    );
                } }
            </Form>
        </div>
    );
};

// set display name
accessManagementUserGroupsCreateModeUsersTab.displayName = 'accessManagementUserGroupsCreateModeUsersTab';

// set default props
accessManagementUserGroupsCreateModeUsersTab.defaultProps = {};

// prop types
accessManagementUserGroupsCreateModeUsersTab.propTypes = {};
